// @ts-nocheck
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { trpc } from '@/lib/trpc';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ComboboxConfigMaster } from '@/components/shared/ComboboxConfigMaster';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Calculator,
  Layers,
  Sparkles,
  Printer,
  Scissors,
  Box,
  FileText,
  Loader2,
  CheckCircle2,
  RotateCw,
  Wand2,
  ListChecks,
  Plus,
  Component,
  Save,
  X,
  PrinterIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { calculateImposition } from '@/utils/imposition-calculator';
import { computeCatalogPriceFromCost } from '@/shared/helpers/product-costing.helper';
import type { EstimatingComponent } from '@/pages/estimating/calculator/components/MachineTradeoffSimulator';

const BOX_PRESETS = [
  { code: 'ECMA_A20_10', name: 'ECMA A20.10 - Hộp Đáy Khóa Tự Động (Auto-Lock)', formula: (l: number, w: number, h: number) => ({ flatW: (w + h) * 2 + 20, flatL: l + h * 2 + 30 }) },
  { code: 'ECMA_A20_20', name: 'ECMA A20.20 - Hộp Nắp Gài Đáy Gài (Tuck End)', formula: (l: number, w: number, h: number) => ({ flatW: (w + h) * 2 + 15, flatL: l + h * 2 + 25 }) },
  { code: 'ECMA_C20', name: 'ECMA C20 - Hộp Âm Dương Nắp Chụp (Telescope)', formula: (l: number, w: number, h: number) => ({ flatW: w + h * 2 + 20, flatL: l + h * 2 + 20 }) },
  { code: 'FEFCO_0201', name: 'FEFCO 0201 - Thùng Carton Sóng Bồi (RSC)', formula: (l: number, w: number, h: number) => ({ flatW: (l + w) * 2 + 40, flatL: h + w + 20 }) },
  { code: 'FEFCO_0427', name: 'FEFCO 0427 - Hộp Gài Tai Pizza / Mailer Box', formula: (l: number, w: number, h: number) => ({ flatW: w * 2 + h * 3 + 30, flatL: l + h * 4 + 40 }) },
  { code: 'CUSTOM', name: 'Tùy Chỉnh Kích Thước Thủ Công (Custom)', formula: (l: number, w: number, h: number) => ({ flatW: 260, flatL: 380 }) },
];

const MAX_SVG_PREVIEW_CELLS = 64;

export interface ComputeEstimatingParams {
  components: EstimatingComponent[];
  targetOrderQty: number;
  marginPercent: number;
  assemblyCostPerUnit: number;
  packagingType: string;
  hasWindowPatching: boolean;
  primaryPress: any;
  pcsPerBox?: number | null;
  pricingMode?: 'MOQ_STANDARD' | 'EXACT_BATCH';
  costingSource?: 'MASTER_BOM' | 'PREPRESS_SIMULATOR';
  masterDecomposed?: any | null;
}

export function computeEstimatingSummary({
  components,
  targetOrderQty,
  marginPercent,
  assemblyCostPerUnit,
  packagingType,
  hasWindowPatching,
  primaryPress,
  pcsPerBox,
  pricingMode = 'EXACT_BATCH',
  costingSource = 'PREPRESS_SIMULATOR',
  masterDecomposed = null,
}: ComputeEstimatingParams) {
  const safeOrderQty = Math.max(1, Math.round(Number(targetOrderQty) || 1));
  const moqQty = Math.max(1, Number(primaryPress?.batchSize) || 1000);
  const calcQty =
    pricingMode === 'MOQ_STANDARD' && safeOrderQty < moqQty
      ? moqQty
      : safeOrderQty;
  const scaleFactor = safeOrderQty / calcQty;

  let totalPaperCost = 0;
  let totalPlatesCost = 0;
  let totalPrintCost = 0;
  let totalPostPressCost = 0;

  const componentCalculations = components.map((comp) => {
    const safeFlatW = Math.max(10, Number(comp.flatWidth) || 100);
    const safeFlatL = Math.max(10, Number(comp.flatLength) || 100);
    const sheetW = Number(comp.sheetWidth || primaryPress?.maxSheetWidth || 720);
    const sheetL = Number(comp.sheetLength || primaryPress?.maxSheetLength || 1020);
    const imp = calculateImposition(sheetW, sheetL, safeFlatW, safeFlatL, 15, 3, 5);

    const hasManualUps = comp.manualUps != null && Number(comp.manualUps) > 0;
    const ups = hasManualUps
      ? Math.max(1, Math.round(Number(comp.manualUps)))
      : Math.max(1, imp.outs);

    const cols = hasManualUps
      ? Math.max(1, Math.ceil(Math.sqrt(ups)))
      : Math.max(1, imp.cols || 1);
    const rows = hasManualUps
      ? Math.max(1, Math.ceil(ups / cols))
      : Math.max(1, imp.rows || 1);

    const isWorkAndTurn =
      comp.printScheme === 'WORK_AND_TURN' || comp.printScheme === 'WORK_AND_TUMBLE';
    const effectiveUps = isWorkAndTurn ? ups * 2 : ups;
    const netSheets = Math.ceil(calcQty / effectiveUps);

    const setupWasteSheets = isWorkAndTurn
      ? 35
      : comp.printScheme === 'SHEETWISE_AB'
        ? 70
        : 40;
    const runWasteSheets = Math.ceil(netSheets * 0.02);
    const grossSheets = netSheets + setupWasteSheets + runWasteSheets;

    const fullPressAreaM2 = (sheetW * sheetL) / 1_000_000;
    const fittedCutAreaM2 =
      ((safeFlatW + 20) * (safeFlatL + 20) * ups) / 1_000_000;
    const hasExplicitSheet = Boolean(comp.sheetWidth && comp.sheetLength);
    const sheetAreaM2 =
      hasManualUps && !hasExplicitSheet
        ? Math.max(0.02, Math.min(fullPressAreaM2, fittedCutAreaM2))
        : fullPressAreaM2;

    const usedAreaM2 = (safeFlatW * safeFlatL * ups) / 1_000_000;
    const effectiveWastePct = hasManualUps
      ? Number(
          Math.max(
            0,
            Math.min(95, ((sheetAreaM2 - usedAreaM2) / Math.max(sheetAreaM2, 0.0001)) * 100),
          ).toFixed(1),
        )
      : imp.wastePct;

    const sheetWeightKg = (sheetAreaM2 * (Number(comp.gsm) || 300)) / 1000;
    const rawPaperCost = Math.round(
      grossSheets * sheetWeightKg * (Number(comp.paperPriceKg) || 28500),
    );

    const plateCount = isWorkAndTurn
      ? (Number(comp.colorsFront) || 0) + (Number(comp.spotColorsFront) || 0)
      : (Number(comp.colorsFront) || 0) +
        (Number(comp.spotColorsFront) || 0) +
        (Number(comp.colorsBack) || 0) +
        (Number(comp.spotColorsBack) || 0);
    const plateUnitPrice = Number(
      primaryPress?.plateCost ?? primaryPress?.plateCostPerPlate ?? 120_000,
    );
    const rawPlatesCost = plateCount * plateUnitPrice;

    const printMakeReady = Number(primaryPress?.cmykBatchCost ?? 1_200_000);
    const printClickRate = Number(
      primaryPress?.cmykRunOnCost ?? primaryPress?.runOnCostPerUnit ?? 150,
    );
    const rawPrintCost = printMakeReady + grossSheets * printClickRate;

    let rawPostPressCost = 0;
    const stagesFlow: any[] = [
      { name: 'Xén Giấy Mộc', cost: Math.round(120_000 * scaleFactor) },
      {
        name: `In Offset (${comp.colorsFront}M)`,
        cost: Math.round(rawPrintCost * scaleFactor),
      },
    ];
    rawPostPressCost += 120_000;

    if (comp.laminationSides !== 'NONE') {
      const lamCost = Math.max(
        250_000,
        Math.round(
          grossSheets *
            sheetAreaM2 *
            2_800 *
            (comp.laminationSides === 'TWO_SIDES' ? 1.9 : 1),
        ),
      );
      rawPostPressCost += lamCost;
      stagesFlow.push({
        name: 'Cán Màng BOPP',
        cost: Math.round(lamCost * scaleFactor),
      });
    }

    if (comp.foilGold || comp.foilSilver) {
      const foilMoldCost = 350_000;
      const foilRunCost = Math.max(300_000, grossSheets * 120);
      rawPostPressCost += foilMoldCost + foilRunCost;
      stagesFlow.push({
        name: 'Ép Kim Nhũ',
        cost: Math.round((foilMoldCost + foilRunCost) * scaleFactor),
      });
    }

    if (comp.hasSpotUv) {
      const uvCost = Math.max(350_000, grossSheets * 180);
      rawPostPressCost += uvCost;
      stagesFlow.push({
        name: 'Phủ Spot UV',
        cost: Math.round(uvCost * scaleFactor),
      });
    }

    if (comp.mountingType === 'RIGID_BOARD') {
      const mountCost = Math.max(500_000, grossSheets * 1_200);
      rawPostPressCost += mountCost;
      stagesFlow.push({
        name: 'Bồi Bìa Cứng 2mm',
        cost: Math.round(mountCost * scaleFactor),
      });
    }

    let rawMoldCost = 0;
    if (comp.diecutType && comp.diecutType !== 'NONE') {
      rawMoldCost = 800_000;
      const diecutRunCost = Math.max(350_000, grossSheets * 90);
      rawPostPressCost += rawMoldCost + diecutRunCost;
      stagesFlow.push({
        name: 'Bế Khuôn Tự Động',
        cost: Math.round((rawMoldCost + diecutRunCost) * scaleFactor),
      });
    }

    const paperCost = Math.round(rawPaperCost * scaleFactor);
    const platesCost = Math.round(rawPlatesCost * scaleFactor);
    const printCost = Math.round(rawPrintCost * scaleFactor);
    const postPressCost = Math.round(rawPostPressCost * scaleFactor);
    const moldCost = Math.round(rawMoldCost * scaleFactor);

    totalPaperCost += paperCost;
    totalPlatesCost += platesCost;
    totalPrintCost += printCost;
    totalPostPressCost += postPressCost;

    const compTotalCost = paperCost + platesCost + printCost + postPressCost;
    const displayNetSheets = Math.ceil(safeOrderQty / effectiveUps);
    const displayRunWaste = Math.ceil(displayNetSheets * 0.02);
    const displaySetupWaste =
      pricingMode === 'MOQ_STANDARD' && safeOrderQty < moqQty
        ? Math.max(1, Math.round(setupWasteSheets * scaleFactor))
        : setupWasteSheets;
    const displayGrossSheets =
      displayNetSheets + displaySetupWaste + displayRunWaste;

    return {
      ...comp,
      ups,
      hasManualUps,
      imposition: imp,
      layoutType: imp.layoutType,
      wastePct: effectiveWastePct,
      cols,
      rows,
      netSheets: displayNetSheets,
      grossSheets: displayGrossSheets,
      setupWasteSheets: displaySetupWaste,
      runWasteSheets: displayRunWaste,
      paperCost,
      platesCost,
      printCost,
      postPressCost,
      compTotalCost,
      moldCost,
      stagesFlow,
    };
  });

  const effectivePcsPerBox =
    pcsPerBox != null && Number(pcsPerBox) > 0 ? Math.round(Number(pcsPerBox)) : 200;
  const cartonCount = Math.max(1, Math.ceil(calcQty / effectivePcsPerBox));
  const pkgUpper = String(packagingType || 'CARTON_5L').toUpperCase();
  let rawPackagingTotal = cartonCount * 35_000;
  if (pkgUpper === 'NONE' || pkgUpper === 'POLY_TIE' || pkgUpper.includes('BUỘC') || pkgUpper.includes('MÀNG')) {
    rawPackagingTotal = Math.max(5_000, calcQty * 50);
  } else if (pkgUpper === 'PALLET' || pkgUpper.includes('PALLET')) {
    rawPackagingTotal = Math.max(1, Math.ceil(calcQty / 5000)) * 350_000;
  } else if (pkgUpper === 'CARTON_3L' || pkgUpper.includes('3') || pkgUpper.includes('KRAFT')) {
    rawPackagingTotal = cartonCount * 20_000;
  }

  const rawAssemblyTotal = calcQty * Math.max(0, Number(assemblyCostPerUnit) || 0);
  const rawWindowTotal = hasWindowPatching ? Math.max(400_000, calcQty * 250) : 0;
  const generalFinishingCost = Math.round(
    (rawAssemblyTotal + rawPackagingTotal + rawWindowTotal) * scaleFactor,
  );

  if (costingSource === 'MASTER_BOM' && Number(masterDecomposed?.summary?.unitCost) > 0) {
    const masterUnitCost = Math.round(Number(masterDecomposed.summary.unitCost));
    const decompTargetQty = Math.max(1, Number(masterDecomposed.targetQuantity) || safeOrderQty);
    const masterTotalFactoryCost =
      decompTargetQty === safeOrderQty && Number(masterDecomposed.summary.totalCost) > 0
        ? Math.round(Number(masterDecomposed.summary.totalCost))
        : Math.round(masterUnitCost * safeOrderQty);
    const decompTotalBasis = Math.max(
      1,
      Number(masterDecomposed.summary.totalCost) || masterUnitCost * decompTargetQty,
    );
    const costRatio = masterTotalFactoryCost / decompTotalBasis;
    const decompComps =
      Array.isArray(masterDecomposed.components) && masterDecomposed.components.length > 0
        ? masterDecomposed.components
        : [];

    const masterComponentCalcs = componentCalculations.map((c, idx) => {
      const dc = decompComps[idx] || (idx === 0 ? decompComps[0] : null);
      const rawMat = (dc?.materials || []).reduce(
        (sum: number, m: any) => sum + (Number(m.calculatedCost) || 0),
        0,
      );
      const rawRoutings = Array.isArray(dc?.routings) ? dc.routings : [];
      const rawPrint = rawRoutings
        .filter((r: any) => {
          const grp = String(r.group || '').toUpperCase();
          const rName = String(r.processName || '').toLowerCase();
          return grp === 'IN' || rName.includes('cmyk') || rName.includes('in offset');
        })
        .reduce((sum: number, r: any) => sum + (Number(r.calculatedCost) || 0), 0);
      const rawTotalProc = rawRoutings.reduce(
        (sum: number, r: any) => sum + (Number(r.calculatedCost) || 0),
        0,
      );
      const rawPost = Math.max(0, rawTotalProc - rawPrint);

      const paperCost = decompComps.length > 0 ? Math.round(rawMat * costRatio) : (idx === 0 ? Math.round((Number(masterDecomposed.summary?.totalMaterialCost) || 0) * costRatio) : 0);
      const printCost = decompComps.length > 0 ? Math.round(rawPrint * costRatio) : 0;
      const postPressCost = decompComps.length > 0 ? Math.round(rawPost * costRatio) : (idx === 0 ? Math.round((Number(masterDecomposed.summary?.totalProcessCost) || 0) * costRatio) : 0);
      const platesCost = 0;
      const resolvedUps = Number(dc?.outUp) > 0 ? Math.round(Number(dc.outUp)) : c.ups;
      const resolvedStages =
        rawRoutings.length > 0
          ? rawRoutings.map((r: any) => ({
              name: r.processName || 'Công đoạn',
              cost: Math.round((Number(r.calculatedCost) || 0) * costRatio),
            }))
          : c.stagesFlow;

      return {
        ...c,
        name: dc?.name || c.name,
        ups: resolvedUps,
        paperCost,
        platesCost,
        printCost,
        postPressCost,
        moldCost: 0,
        compTotalCost: paperCost + platesCost + printCost + postPressCost,
        stagesFlow: resolvedStages,
      };
    });

    if (masterComponentCalcs.length > 0) {
      const currentSum = masterComponentCalcs.reduce((s, c) => s + c.compTotalCost, 0);
      const delta = masterTotalFactoryCost - currentSum;
      if (delta !== 0) {
        const last = masterComponentCalcs[masterComponentCalcs.length - 1];
        if (last.postPressCost + delta >= 0) {
          last.postPressCost += delta;
        } else if (last.printCost + delta >= 0) {
          last.printCost += delta;
        } else {
          last.paperCost = Math.max(0, last.paperCost + delta);
        }
        last.compTotalCost =
          last.paperCost + last.platesCost + last.printCost + last.postPressCost;
      }
    }

    const mPaper = masterComponentCalcs.reduce((s, c) => s + c.paperCost, 0);
    const mPlates = masterComponentCalcs.reduce((s, c) => s + c.platesCost, 0);
    const mPrint = masterComponentCalcs.reduce((s, c) => s + c.printCost, 0);
    const mPost = masterComponentCalcs.reduce((s, c) => s + c.postPressCost, 0);
    const mFinishing = Math.max(0, masterTotalFactoryCost - (mPaper + mPlates + mPrint + mPost));
    const masterUnitPrice = computeCatalogPriceFromCost(
      masterUnitCost,
      Number(marginPercent) || 0,
    );
    const masterSuggestedPrice = Math.round(masterUnitPrice * safeOrderQty);

    return {
      componentCalculations: masterComponentCalcs,
      totalPaperCost: mPaper,
      totalPlatesCost: mPlates,
      totalPrintCost: mPrint,
      totalPostPressCost: mPost,
      generalFinishingCost: mFinishing,
      totalFactoryCost: masterTotalFactoryCost,
      unitCost: masterUnitCost,
      suggestedPrice: masterSuggestedPrice,
      unitPrice: masterUnitPrice,
      moqQty,
      pricingMode,
      costingSource: 'MASTER_BOM' as const,
    };
  }

  const totalFactoryCost =
    totalPaperCost +
    totalPlatesCost +
    totalPrintCost +
    totalPostPressCost +
    generalFinishingCost;
  const unitCost = Math.round(totalFactoryCost / safeOrderQty);

  const unitPrice = computeCatalogPriceFromCost(unitCost, Number(marginPercent) || 0);
  const suggestedPrice = Math.round(unitPrice * safeOrderQty);

  return {
    componentCalculations,
    totalPaperCost,
    totalPlatesCost,
    totalPrintCost,
    totalPostPressCost,
    generalFinishingCost,
    totalFactoryCost,
    unitCost,
    suggestedPrice,
    unitPrice,
    moqQty,
    pricingMode,
    costingSource: 'PREPRESS_SIMULATOR' as const,
  };
}

interface QuotationEstimatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  quotationDetailId: string;
  orderQty?: number;
  productName?: string;
  specs?: string;
  onSuccess?: () => void;
}

export function QuotationEstimatingModal({
  isOpen,
  onClose,
  quotationDetailId,
  orderQty = 1000,
  productName = '',
  specs = '',
  onSuccess,
}: QuotationEstimatingModalProps) {
  const queryClient = useQueryClient();

  // 1. QUERY DRAFT & HYDRATED PARAMS FROM BACKEND
  const draftQuery = useQuery({
    ...trpc.estimating.getDraft.queryOptions({ quotationDetailId }),
    enabled: isOpen && !!quotationDetailId,
  });

  const draftData = draftQuery.data;
  const isLoadingDraft = draftQuery.isLoading;

  // 1.1 QUERY DYNAMIC PRICING CONFIGS FROM DATABASE (ZERO HARDCODE)
  const configsQuery = useQuery({
    ...trpc.pricingConfig.getAllConfigs.queryOptions(),
    enabled: isOpen,
  });
  const machines = (configsQuery.data?.machines || []) as any[];
  const primaryPress = machines.find((m: any) => m.code === 'OFFSET_72X102') || machines[0] || {
    code: 'OFFSET_72X102',
    name: 'Máy in Offset 4 màu khổ 72x102',
    maxSheetWidth: 720,
    maxSheetLength: 1020,
    cmykBatchCost: 1_200_000,
    runOnCostPerUnit: 150,
    plateCostPerPlate: 120_000,
    batchSize: 1000,
  };

  // Global State
  const [targetOrderQty, setTargetOrderQty] = useState<number>(orderQty || 1000);
  const [activeCompId, setActiveCompId] = useState<string>('comp-0');
  const [marginPercent, setMarginPercent] = useState<number>(20);
  const [assemblyCostPerUnit, setAssemblyCostPerUnit] = useState<number>(350);
  const [packagingType, setPackagingType] = useState<string>('CARTON_5L');
  const [pcsPerBox, setPcsPerBox] = useState<number | null>(null);
  const [pricingMode, setPricingMode] = useState<'MOQ_STANDARD' | 'EXACT_BATCH'>(
    (orderQty || 1000) < 1000 ? 'MOQ_STANDARD' : 'EXACT_BATCH',
  );
  const [costingSource, setCostingSource] = useState<'MASTER_BOM' | 'PREPRESS_SIMULATOR'>('PREPRESS_SIMULATOR');
  const [hasWindowPatching, setHasWindowPatching] = useState<boolean>(false);
  const [isProcessMatrixOpen, setIsProcessMatrixOpen] = useState<boolean>(false);
  const [isPrintPreviewOpen, setIsPrintPreviewOpen] = useState<boolean>(false);

  const linkedProductId = String(draftData?.quotationDetail?.productId || draftData?.product?.id || '');
  const hasMasterBomDecomposition =
    Number((draftData as any)?.decomposedStandard?.summary?.unitCost || 0) > 0;
  const safeTargetQty = Math.max(1, Math.round(Number(targetOrderQty) || 1));
  const initialDetailQty = Math.max(
    1,
    Math.round(Number(draftData?.quotationDetail?.quantity || orderQty || 1000)),
  );

  const exactDecomposedQuery = useQuery({
    ...trpc.estimating.decomposeProductByQty.queryOptions({
      productId: linkedProductId,
      quantity: safeTargetQty,
    }),
    enabled:
      isOpen &&
      Boolean(linkedProductId) &&
      costingSource === 'MASTER_BOM' &&
      pricingMode === 'EXACT_BATCH' &&
      safeTargetQty !== 1000 &&
      safeTargetQty !== initialDetailQty,
  });

  const activeMasterDecomposed = useMemo(() => {
    if (!draftData) return null;
    const std = (draftData as any)?.decomposedStandard || null;
    const exact = (draftData as any)?.decomposedExact || null;
    if (pricingMode === 'MOQ_STANDARD' || safeTargetQty === 1000) {
      return std || exact;
    }
    if (safeTargetQty === initialDetailQty) {
      return exact || std;
    }
    return exactDecomposedQuery.data || exact || std;
  }, [draftData, pricingMode, safeTargetQty, initialDetailQty, exactDecomposedQuery.data]);

  // Components State
  const [components, setComponents] = useState<EstimatingComponent[]>([
    {
      id: 'comp-0',
      name: 'Thân Hộp Chính',
      partType: 'BODY',
      flatWidth: 260,
      flatLength: 380,
      grainDirection: 'MD',
      isRotated90: false,
      boxDimensions: { length: 120, width: 80, height: 40 },
      boxPresetCode: 'ECMA_A20_10',
      paperType: 'Ivory',
      gsm: 300,
      paperPriceKg: 28500,
      printScheme: 'WORK_AND_TURN',
      colorsFront: 4,
      spotColorsFront: 0,
      colorsBack: 0,
      spotColorsBack: 0,
      laminationSides: 'ONE_SIDE',
      laminationType: 'BOPP_MATTE',
      mountingType: 'NONE',
      foilGold: false,
      foilSilver: false,
      foilHolo: false,
      foilRoseGold: false,
      foilAreaCm2: 0,
      hasSpotUv: false,
      hasUvTexture: false,
      hasVarnish: false,
      hasEmbossing: false,
      hasDebossing: false,
      diecutType: 'STANDARD',
      hasAutoStripping: true,
    }
  ]);

  // SMART HYDRATION EFFECT WHEN DRAFT ARRIVES
  useEffect(() => {
    if (!draftData) return;

    const hydratedQty = draftData.quotationDetail?.quantity || orderQty || 1000;
    setTargetOrderQty(hydratedQty);

    const h = draftData.hydratedDefaults as any;
    if (h?.pcsPerBox != null) {
      setPcsPerBox(h.pcsPerBox);
    }
    const hasDecomp = Number((draftData as any)?.decomposedStandard?.summary?.unitCost || 0) > 0;
    const savedFinParams = (draftData.costEstimate?.financialParams || {}) as any;
    const savedCostingSource = savedFinParams?.costingSource;
    setCostingSource(
      hasDecomp && savedCostingSource !== 'PREPRESS_SIMULATOR'
        ? 'MASTER_BOM'
        : 'PREPRESS_SIMULATOR',
    );

    if (draftData.costEstimate?.components && draftData.costEstimate.components.length > 0) {
      const finParams = savedFinParams;
      const restored = draftData.costEstimate.components.map((c: any, idx: number) => {
        const mat = c.materials?.[0];
        const prepress = c.prepressParams || {};
        const restoredManualUps =
          prepress.manualUps !== undefined
            ? prepress.manualUps
            : c.outUp != null && Number(c.outUp) > 0
              ? Math.round(Number(c.outUp))
              : h?.outs ?? null;
        return {
          id: `comp-${idx}`,
          name: c.name || `Chi tiết #${idx + 1}`,
          partType: idx === 0 ? 'BODY' : (idx === 1 ? 'TRAY' : 'COVER'),
          flatWidth: c.flatWidth || h?.flatWidth || 260,
          flatLength: c.flatLength || h?.flatLength || 380,
          manualUps: restoredManualUps,
          sheetWidth: prepress.sheetWidth ?? h?.sheetWidth ?? null,
          sheetLength: prepress.sheetLength ?? h?.sheetLength ?? null,
          grainDirection: prepress.grainDirection || 'MD',
          isRotated90: false,
          boxDimensions: prepress.boxDimensions || h?.boxDimensions || { length: 120, width: 80, height: 40 },
          boxPresetCode: 'ECMA_A20_10',
          paperType: mat?.materialName?.includes('Couche') ? 'Couche' : (mat?.materialName?.includes('Duplex') ? 'Duplex' : 'Ivory'),
          gsm: 300,
          paperPriceKg: mat?.unitPrice || 28500,
          printScheme: prepress.printScheme || (c.printTech?.includes('WORK') ? 'WORK_AND_TURN' : 'SINGLE_SIDED'),
          colorsFront: 4,
          spotColorsFront: 0,
          colorsBack: 0,
          spotColorsBack: 0,
          laminationSides: 'ONE_SIDE',
          laminationType: 'BOPP_MATTE',
          mountingType: 'NONE',
          foilGold: false,
          foilSilver: false,
          foilHolo: false,
          foilRoseGold: false,
          foilAreaCm2: 0,
          hasSpotUv: false,
          hasUvTexture: false,
          hasVarnish: false,
          hasEmbossing: false,
          hasDebossing: false,
          diecutType: prepress.diecutType || h?.diecutType || 'STANDARD',
          hasAutoStripping: (prepress.diecutType || h?.diecutType) !== 'NONE',
        } as EstimatingComponent;
      });
      setComponents(restored);
      setActiveCompId(restored[0].id);
      if (draftData.costEstimate.marginPercent != null && Number(draftData.costEstimate.marginPercent) > 0) {
        setMarginPercent(Number(draftData.costEstimate.marginPercent));
      } else if (h?.marginPercent != null) {
        setMarginPercent(Number(h.marginPercent));
      }
      if (savedCostingSource && finParams.tradeoffPlan?.pricingMode) {
        setPricingMode(finParams.tradeoffPlan.pricingMode);
      } else {
        setPricingMode(h?.isNonMoq || hydratedQty >= 1000 ? 'EXACT_BATCH' : 'MOQ_STANDARD');
      }
      if (finParams.tradeoffPlan?.packagingType) {
        setPackagingType(finParams.tradeoffPlan.packagingType);
      }
      if (finParams.tradeoffPlan?.assemblyCostPerUnit !== undefined) {
        setAssemblyCostPerUnit(Number(finParams.tradeoffPlan.assemblyCostPerUnit));
      }
    } else if (h) {
      if (h.marginPercent != null) {
        setMarginPercent(Number(h.marginPercent));
      }
      setAssemblyCostPerUnit(h.hasAssembly === false ? 0 : 350);
      setPackagingType(h.packagingType || 'CARTON_5L');
      setPricingMode(h.isNonMoq || hydratedQty >= 1000 ? 'EXACT_BATCH' : 'MOQ_STANDARD');

      if (draftData.product?.isComposite && Array.isArray(draftData.product.components) && draftData.product.components.length > 0) {
        const compList: EstimatingComponent[] = draftData.product.components.map((pc: any, idx: number) => ({
          id: `comp-${idx}`,
          name: pc.componentName || `Chi tiết #${idx + 1}`,
          partType: idx === 0 ? 'BODY' : (idx === 1 ? 'TRAY' : 'COVER'),
          flatWidth: Number(pc.flatWidth) > 0 ? Number(pc.flatWidth) : h.flatWidth,
          flatLength: Number(pc.flatLength) > 0 ? Number(pc.flatLength) : h.flatLength,
          manualUps:
            pc.outUp != null && Number(pc.outUp) > 0
              ? Math.round(Number(pc.outUp))
              : h.outs ?? null,
          sheetWidth: h.sheetWidth ?? null,
          sheetLength: h.sheetLength ?? null,
          grainDirection: 'MD',
          isRotated90: false,
          boxDimensions: {
            length: Number(pc.length) || h.boxDimensions?.length || 120,
            width: Number(pc.width) || h.boxDimensions?.width || 80,
            height: Number(pc.height) || h.boxDimensions?.height || 40,
          },
          boxPresetCode: 'ECMA_A20_10',
          paperType: h.paperType,
          gsm: h.gsm,
          paperPriceKg: h.paperPriceKg,
          printScheme: h.printScheme,
          colorsFront: Number(pc.frontColors) || h.colorsFront,
          spotColorsFront: Number(pc.spotColors) || h.spotColorsFront,
          colorsBack: Number(pc.backColors) || h.colorsBack,
          spotColorsBack: h.spotColorsBack,
          laminationSides: h.laminationSides,
          laminationType: h.laminationType,
          mountingType: h.mountingType,
          foilGold: h.foilGold,
          foilSilver: h.foilSilver,
          foilHolo: h.foilHolo,
          foilRoseGold: h.foilRoseGold,
          foilAreaCm2: h.foilAreaCm2,
          hasSpotUv: Boolean(pc.hasUv ?? h.hasSpotUv),
          hasUvTexture: h.hasUvTexture,
          hasVarnish: h.hasVarnish,
          hasEmbossing: h.hasEmbossing,
          hasDebossing: h.hasDebossing,
          diecutType: h.diecutType,
          hasAutoStripping: h.hasAutoStripping,
        }));
        setComponents(compList);
        setActiveCompId(compList[0].id);
      } else {
        const initial: EstimatingComponent = {
          id: 'comp-0',
          name: h.productName || productName || 'Thành phần chính',
          partType: 'BODY',
          flatWidth: h.flatWidth,
          flatLength: h.flatLength,
          manualUps: h.outs != null && Number(h.outs) > 0 ? Math.round(Number(h.outs)) : null,
          sheetWidth: h.sheetWidth ?? null,
          sheetLength: h.sheetLength ?? null,
          grainDirection: 'MD',
          isRotated90: false,
          boxDimensions: h.boxDimensions,
          boxPresetCode: 'ECMA_A20_10',
          paperType: h.paperType,
          gsm: h.gsm,
          paperPriceKg: h.paperPriceKg,
          printScheme: h.printScheme,
          colorsFront: h.colorsFront,
          spotColorsFront: h.spotColorsFront,
          colorsBack: h.colorsBack,
          spotColorsBack: h.spotColorsBack,
          laminationSides: h.laminationSides,
          laminationType: h.laminationType,
          mountingType: h.mountingType,
          foilGold: h.foilGold,
          foilSilver: h.foilSilver,
          foilHolo: h.foilHolo,
          foilRoseGold: h.foilRoseGold,
          foilAreaCm2: h.foilAreaCm2,
          hasSpotUv: h.hasSpotUv,
          hasUvTexture: h.hasUvTexture,
          hasVarnish: h.hasVarnish,
          hasEmbossing: h.hasEmbossing,
          hasDebossing: h.hasDebossing,
          diecutType: h.diecutType,
          hasAutoStripping: h.hasAutoStripping,
        };
        setComponents([initial]);
        setActiveCompId('comp-0');
      }
    }
  }, [draftData, productName, orderQty]);

  const activeComp = useMemo(() => {
    return components.find(c => c.id === activeCompId) || components[0] || {} as EstimatingComponent;
  }, [components, activeCompId]);

  const updateActiveComponent = (patch: Partial<EstimatingComponent>) => {
    setComponents(prev => prev.map(c => c.id === activeCompId ? { ...c, ...patch } : c));
  };

  const handleBoxDimensionChange = (field: 'length' | 'width' | 'height', val: number) => {
    const nextDim = { ...activeComp.boxDimensions, [field]: val };
    const preset = BOX_PRESETS.find(p => p.code === activeComp.boxPresetCode) || BOX_PRESETS[0];
    if (preset.code !== 'CUSTOM') {
      const calc = preset.formula(nextDim.length, nextDim.width, nextDim.height);
      updateActiveComponent({ boxDimensions: nextDim, flatWidth: calc.flatW, flatLength: calc.flatL });
    } else {
      updateActiveComponent({ boxDimensions: nextDim });
    }
  };

  const handlePresetChange = (presetCode: string) => {
    const preset = BOX_PRESETS.find(p => p.code === presetCode) || BOX_PRESETS[0];
    if (preset.code !== 'CUSTOM') {
      const calc = preset.formula(activeComp.boxDimensions.length, activeComp.boxDimensions.width, activeComp.boxDimensions.height);
      updateActiveComponent({ boxPresetCode: presetCode, flatWidth: calc.flatW, flatLength: calc.flatL });
    } else {
      updateActiveComponent({ boxPresetCode: presetCode });
    }
  };

  const handleAddComponent = () => {
    const newId = `comp-${Date.now()}`;
    const newComp: EstimatingComponent = {
      id: newId,
      name: `Chi Tiết #${components.length + 1}`,
      partType: 'CUSTOM',
      flatWidth: 200,
      flatLength: 280,
      grainDirection: 'MD',
      isRotated90: false,
      boxDimensions: { length: 100, width: 60, height: 30 },
      boxPresetCode: 'ECMA_A20_10',
      paperType: 'Ivory',
      gsm: 300,
      paperPriceKg: 28500,
      printScheme: 'SINGLE_SIDED',
      colorsFront: 1,
      spotColorsFront: 0,
      colorsBack: 0,
      spotColorsBack: 0,
      laminationSides: 'NONE',
      laminationType: 'BOPP_MATTE',
      mountingType: 'NONE',
      foilGold: false,
      foilSilver: false,
      foilHolo: false,
      foilRoseGold: false,
      foilAreaCm2: 0,
      hasSpotUv: false,
      hasUvTexture: false,
      hasVarnish: false,
      hasEmbossing: false,
      hasDebossing: false,
      diecutType: 'STANDARD',
      hasAutoStripping: true,
    };
    setComponents(prev => [...prev, newComp]);
    setActiveCompId(newId);
  };

  const handleRemoveComponent = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (components.length <= 1) {
      toast.error('Sản phẩm phải có ít nhất 1 thành phần!');
      return;
    }
    const remaining = components.filter(c => c.id !== id);
    setComponents(remaining);
    if (activeCompId === id) {
      setActiveCompId(remaining[0].id);
    }
  };

  // 2. MATHEMATICAL CALCULATION ENGINE (UNIFIED MASTER BOM + PREPRESS COSTING)
  const calculationSummary = useMemo(
    () =>
      computeEstimatingSummary({
        components,
        targetOrderQty,
        marginPercent,
        assemblyCostPerUnit,
        packagingType,
        hasWindowPatching,
        primaryPress,
        pcsPerBox,
        pricingMode,
        costingSource: hasMasterBomDecomposition ? costingSource : 'PREPRESS_SIMULATOR',
        masterDecomposed: activeMasterDecomposed,
      }),
    [
      components,
      targetOrderQty,
      marginPercent,
      assemblyCostPerUnit,
      packagingType,
      hasWindowPatching,
      primaryPress,
      pcsPerBox,
      pricingMode,
      costingSource,
      hasMasterBomDecomposition,
      activeMasterDecomposed,
    ],
  );

  // 3. APPLY ESTIMATE MUTATION
  const applyMutation = useMutation({
    ...trpc.estimating.applyEstimateToQuotationDetail.mutationOptions(),
    onSuccess: () => {
      toast.success('Đã lưu dự toán và giá vốn tham khảo. Giá bán trên báo giá giữ nguyên.');
      queryClient.invalidateQueries({ queryKey: trpc.quotations.getById.queryKey() });
      queryClient.invalidateQueries({ queryKey: ['quotations'] });
      if (onSuccess) onSuccess();
      onClose();
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi lưu dự toán: ${err.message}`);
    }
  });

  // 4. SYNC TO PRODUCT MASTER DATA MUTATION
  const syncProductMutation = useMutation({
    ...trpc.estimating.syncEstimateToProduct.mutationOptions(),
    onSuccess: (data) => {
      toast.success(`Đã cập nhật Master Data Sản Phẩm lên Version #${data.version} thành công!`);
      queryClient.invalidateQueries({ queryKey: trpc.products.list.queryKey() });
      queryClient.invalidateQueries({ queryKey: ['products'] });
      queryClient.invalidateQueries({ queryKey: ['product'] });
      queryClient.invalidateQueries({ queryKey: ['product-decomposition'] });
      queryClient.invalidateQueries({ queryKey: ['product-decomposition-preview'] });
      queryClient.invalidateQueries({ queryKey: trpc.quotations.getById.queryKey() });
    },
    onError: (err: any) => {
      toast.error(`Lỗi khi cập nhật sản phẩm: ${err.message}`);
    }
  });

  const handleSyncToProduct = async () => {
    const pId = draftData?.quotationDetail?.productId || draftData?.product?.id;
    if (!pId) {
      toast.error('Dòng báo giá này chưa liên kết với một bản ghi Sản phẩm trong Master Data!');
      return;
    }
    const effectiveSource = hasMasterBomDecomposition ? costingSource : 'PREPRESS_SIMULATOR';
    const stdDecompCost = Number((draftData as any)?.decomposedStandard?.summary?.unitCost || 0);
    const stdUnitCost =
      effectiveSource === 'MASTER_BOM' && stdDecompCost > 0
        ? Math.round(stdDecompCost)
        : calculationSummary.unitCost;
    // Chỉ lưu thông số + giá vốn vào sản phẩm; không gửi giá bán (đơn giá niêm yết do kinh doanh nhập)
    await syncProductMutation.mutateAsync({
      productId: pId,
      flatWidth: activeComp.flatWidth,
      flatLength: activeComp.flatLength,
      length: activeComp.boxDimensions?.length,
      width: activeComp.boxDimensions?.width,
      height: activeComp.boxDimensions?.height,
      outs: calculationSummary.componentCalculations[0]?.ups || 1,
      unitCost: stdUnitCost,
      standardUnitCost: stdUnitCost,
      costingSource: effectiveSource,
      components: calculationSummary.componentCalculations,
    });
  };

  const handleSaveAndApply = async () => {
    try {
      const effectiveSource = hasMasterBomDecomposition ? costingSource : 'PREPRESS_SIMULATOR';
      await applyMutation.mutateAsync({
        quotationDetailId,
        productName: productName || draftData?.quotationDetail?.productName || 'Hộp Bao Bì Offset',
        orderQty: targetOrderQty,
        unitCost: calculationSummary.unitCost,
        unitPrice: calculationSummary.unitPrice,
        suggestedPrice: calculationSummary.suggestedPrice,
        marginPercent,
        overheadPercent: 0,
        costingSource: effectiveSource,
        components: calculationSummary.componentCalculations,
        tradeoffPlan: {
          pricingMode,
          packagingType,
          assemblyCostPerUnit,
          pcsPerBox,
          costingSource: effectiveSource,
        },
      });
    } catch (e) {
      console.error(e);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
        <DialogContent
          showCloseButton={false}
          className="w-[96vw] max-w-[96vw] sm:max-w-[96vw] lg:max-w-[96vw] xl:max-w-[1580px] h-[92vh] max-h-[95vh] p-0 flex flex-col overflow-hidden bg-slate-100 shadow-2xl border-slate-300 rounded-xl"
        >
          <DialogDescription className="sr-only">
            Bảng tính toán và bóc tách dự toán kỹ thuật chi tiết theo phương pháp Prepress Offset
          </DialogDescription>
          
          {/* ── HEADER MODAL (SINGLE ROW CLEAN BAR) ── */}
          <DialogHeader icon={Calculator} className="flex flex-row items-center justify-between flex-none">
            <div className="flex items-center gap-2 min-w-0">
              <div className="min-w-0">
                <DialogTitle className="flex items-center gap-2 break-words whitespace-normal">
                  <span className="text-slate-500 shrink-0">Dự Toán Kỹ Thuật:</span>
                  <span
                    className="text-amber-700 font-mono break-words whitespace-normal max-w-[420px]"
                    title={productName || draftData?.quotationDetail?.productName || 'Sản Phẩm In'}
                  >
                    {productName || draftData?.quotationDetail?.productName || 'Sản Phẩm In'}
                  </span>
                  {/* Tính giá đang là bản xem thử độc lập (02/11/2026): không đổi giá báo / giá bán, chờ quy trình tính giá của công ty */}
                  <Badge
                    variant="outline"
                    className="text-[9px] bg-amber-50 text-amber-700 border-amber-200 py-0 shrink-0"
                    title="Kết quả chỉ để tham khảo, không thay đổi giá báo và giá bán của sản phẩm"
                    data-testid="estimating-preview-note"
                  >
                    Bản xem thử · chỉ tham khảo
                  </Badge>
                  <span className="text-[10px] text-slate-500 font-mono shrink-0 ml-1">
                    (SL Đặt: <strong className="text-slate-900">{targetOrderQty.toLocaleString()}</strong> pcs)
                  </span>
                </DialogTitle>
              </div>
            </div>

            <div className="flex items-center gap-2.5 shrink-0">
              {Number((draftData?.hydratedDefaults as any)?.standardUnitCost || 0) > 0 && (
                <div className="hidden xl:flex items-center gap-1 bg-indigo-50 px-2.5 py-1 rounded border border-indigo-200 font-mono text-[11px]">
                  <span className="text-indigo-600">Định mức SP:</span>
                  <span className="text-slate-900 font-bold">
                    {Math.round(Number((draftData?.hydratedDefaults as any)?.standardUnitCost)).toLocaleString()} đ
                  </span>
                </div>
              )}
              <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1 rounded border border-slate-200 font-mono text-xs shadow-inner">
                <span className="text-slate-500">Giá Vốn:</span>
                <span className="text-amber-700 font-bold" data-testid="estimating-unit-cost">{calculationSummary.unitCost.toLocaleString()} đ</span>
                <span className="text-slate-600 mx-1">|</span>
                <span className="text-slate-500" title="Chỉ để tham khảo: giá bán do kinh doanh nhập trên báo giá, không bị thay đổi">Giá bán tham khảo:</span>
                <span className="text-emerald-700 font-bold" data-testid="estimating-unit-price">{calculationSummary.unitPrice.toLocaleString()} đ</span>
              </div>

              {(draftData?.quotationDetail?.productId || draftData?.product?.id) && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs font-semibold bg-indigo-950/80 text-indigo-600 border-indigo-700/60 hover:bg-indigo-900 hover:text-slate-900"
                  onClick={handleSyncToProduct}
                  disabled={syncProductMutation.isPending}
                >
                  {syncProductMutation.isPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
                  ) : (
                    <Save className="h-3.5 w-3.5 mr-1" />
                  )}
                  Lưu Giá Vốn Vào SP
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs font-semibold bg-slate-50 text-slate-200 border-slate-200 hover:bg-slate-100 hover:text-slate-900"
                onClick={() => setIsPrintPreviewOpen(true)}
              >
                <PrinterIcon className="h-3.5 w-3.5 mr-1" /> In BM-KT-01
              </Button>

              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-slate-900 font-bold h-7 text-xs gap-1.5 shadow-sm"
                onClick={handleSaveAndApply}
                disabled={applyMutation.isPending || isLoadingDraft}
              >
                {applyMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Đang Lưu...
                  </>
                ) : (
                  <>
                    <Save className="h-3.5 w-3.5" /> Lưu Dự Toán (Giá Vốn)
                  </>
                )}
              </Button>
            </div>
          </DialogHeader>
          <p
            className="flex-none px-4 py-1 text-[11px] leading-snug text-amber-200 bg-amber-900/40 border-b border-amber-800/40"
            data-testid="estimating-price-notice"
          >
            Tính giá đang phát triển, kết quả chỉ để tham khảo. Giá bán do kinh doanh nhập trên báo giá và không bị thay đổi khi lưu dự toán.
          </p>

          {/* ── THANH TAB THÀNH PHẦN (MULTI-COMPONENT SELECTOR) ── */}
          <div className="bg-slate-800 px-4 py-1.5 border-b border-slate-700 flex items-center justify-between flex-none h-10">
            <div className="flex items-center gap-1.5 overflow-x-auto py-0.5">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
                <Component className="h-3.5 w-3.5 text-indigo-400" /> Cấu phần:
              </span>
              {components.map((c, idx) => {
                const isActive = c.id === activeCompId;
                return (
                  <div
                    key={c.id}
                    onClick={() => setActiveCompId(c.id)}
                    className={`cursor-pointer px-3 py-1 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 border ${
                      isActive
                        ? 'bg-indigo-600 text-white border-indigo-400 shadow-sm'
                        : 'bg-slate-700/60 text-slate-300 border-slate-600 hover:bg-slate-700'
                    }`}
                  >
                    <span>✦ {idx + 1}. {c.name}</span>
                    <span className="text-[10px] opacity-80 font-mono font-normal">({c.paperType} {c.gsm}g)</span>
                    {components.length > 1 && (
                      <span
                        onClick={(e) => handleRemoveComponent(c.id, e)}
                        className="ml-1 text-slate-400 hover:text-rose-300 rounded-full hover:bg-rose-900/40 p-0.5"
                      >
                        <X className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </div>
                );
              })}
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddComponent}
                className="h-7 px-2.5 text-xs bg-slate-700/50 border-dashed border-slate-500 text-slate-300 hover:bg-slate-700 hover:text-white"
              >
                <Plus className="h-3 w-3 mr-1" /> Thêm Chi Tiết
              </Button>
            </div>

            <div className="flex items-center gap-2">
              {hasMasterBomDecomposition && (
                <div
                  className="inline-flex items-center rounded-md bg-slate-900/90 p-0.5 border border-slate-700"
                  role="group"
                  aria-label="Nguồn tính giá dự toán"
                >
                  <button
                    type="button"
                    data-testid="costing-source-master-bom"
                    onClick={() => setCostingSource('MASTER_BOM')}
                    className={`px-2.5 py-1 rounded text-[11px] font-bold transition-colors ${
                      costingSource === 'MASTER_BOM'
                        ? 'bg-emerald-600 text-white shadow-xs'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    Theo Định Mức BOM/Routing SP
                  </button>
                  <button
                    type="button"
                    data-testid="costing-source-prepress-sim"
                    onClick={() => setCostingSource('PREPRESS_SIMULATOR')}
                    className={`px-2.5 py-1 rounded text-[11px] font-bold transition-colors ${
                      costingSource === 'PREPRESS_SIMULATOR'
                        ? 'bg-indigo-600 text-white shadow-xs'
                        : 'text-slate-300 hover:text-white'
                    }`}
                  >
                    Mô Phỏng Bài Bình Tùy Chỉnh
                  </button>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsProcessMatrixOpen(true)}
                className="h-7 px-2.5 text-xs bg-indigo-950/40 text-indigo-300 border-indigo-500/40 hover:bg-indigo-900/50 font-medium"
              >
                <ListChecks className="h-3 w-3 mr-1" /> Bảng Quy Trình
              </Button>
            </div>
          </div>

          {/* ── BODY MODAL (3-PANEL ERGONOMIC GRID) ── */}
          <div className="flex-1 overflow-y-auto p-3 grid grid-cols-12 gap-3">
            
            {/* ═══ PANEL 1 (TRÁI - 3.5 CỘT): PREPRESS, SỚ GIẤY & KIỂU IN ═══ */}
            <div className="col-span-12 lg:col-span-4 xl:col-span-3 space-y-2.5">
              <Card className="border-slate-200 shadow-xs bg-white">
                <CardHeader className="bg-slate-50/80 py-2 px-3 border-b">
                  <CardTitle className="text-xs font-bold flex items-center gap-1.5 text-slate-800">
                    <Box className="h-3.5 w-3.5 text-indigo-600" />
                    1. Kiểu Mẫu Hộp & Kích Thước
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-2.5 text-xs">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Mẫu bao bì tiêu chuẩn</Label>
                    <Select
                      value={activeComp.boxPresetCode || 'ECMA_A20_10'}
                      onValueChange={handlePresetChange}
                    >
                      <SelectTrigger className="h-8 text-xs font-medium text-indigo-950 bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {BOX_PRESETS.map(p => (
                          <SelectItem key={p.code} value={p.code} className="text-xs">
                            {p.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-3 gap-2 pt-1 border-t">
                    <div>
                      <Label className="text-[11px] font-semibold text-slate-600 mb-1 block">Dài L (mm)</Label>
                      <Input
                        type="number"
                        value={activeComp.boxDimensions?.length || 120}
                        onChange={e => handleBoxDimensionChange('length', Number(e.target.value))}
                        className="h-8 text-xs font-mono font-bold text-slate-800 px-2 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold text-slate-600 mb-1 block">Rộng W (mm)</Label>
                      <Input
                        type="number"
                        value={activeComp.boxDimensions?.width || 80}
                        onChange={e => handleBoxDimensionChange('width', Number(e.target.value))}
                        className="h-8 text-xs font-mono font-bold text-slate-800 px-2 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-semibold text-slate-600 mb-1 block">Cao H (mm)</Label>
                      <Input
                        type="number"
                        value={activeComp.boxDimensions?.height || 40}
                        onChange={e => handleBoxDimensionChange('height', Number(e.target.value))}
                        className="h-8 text-xs font-mono font-bold text-slate-800 px-2 bg-white"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1 border-t bg-amber-50/70 p-2 rounded-md border border-amber-200">
                    <div>
                      <Label className="text-[11px] font-bold text-amber-900 mb-0.5 block">Trải phẳng W (mm)</Label>
                      <Input
                        type="number"
                        value={activeComp.flatWidth}
                        onChange={e => updateActiveComponent({ flatWidth: Number(e.target.value) })}
                        className="h-8 text-xs font-mono font-bold text-amber-950 bg-white border-amber-300 px-2.5"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] font-bold text-amber-900 mb-0.5 block">Trải phẳng L (mm)</Label>
                      <Input
                        type="number"
                        value={activeComp.flatLength}
                        onChange={e => updateActiveComponent({ flatLength: Number(e.target.value) })}
                        className="h-8 text-xs font-mono font-bold text-amber-950 bg-white border-amber-300 px-2.5"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1 border-t">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Chiều sớ giấy</Label>
                      <Select
                        value={activeComp.grainDirection}
                        onValueChange={(v: any) => updateActiveComponent({ grainDirection: v })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MD">Sớ Dọc (MD)</SelectItem>
                          <SelectItem value="CD">Sớ Ngang (CD)</SelectItem>
                          <SelectItem value="AUTO">Tự Động</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex flex-col justify-end">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => updateActiveComponent({ isRotated90: !activeComp.isRotated90 })}
                        className={`h-8 text-xs font-bold ${activeComp.isRotated90 ? 'bg-amber-100 text-amber-900 border-amber-300' : ''}`}
                      >
                        <RotateCw className="h-3.5 w-3.5 mr-1" /> Xoay 90°
                      </Button>
                    </div>
                  </div>

                  <div className="pt-2 border-t space-y-1.5 bg-indigo-50/50 p-2 rounded-md border border-indigo-200/80">
                    <div className="flex items-center justify-between gap-2">
                      <Label className="text-[11px] font-bold text-indigo-950">
                        Số con / Số bát (ups)
                      </Label>
                      <Badge
                        variant="outline"
                        className={`text-[10px] py-0 ${
                          activeComp.manualUps != null && Number(activeComp.manualUps) > 0
                            ? 'bg-indigo-100 text-indigo-800 border-indigo-300'
                            : 'bg-slate-100 text-slate-600 border-slate-300'
                        }`}
                      >
                        {activeComp.manualUps != null && Number(activeComp.manualUps) > 0
                          ? 'Theo SP gốc / Khóa tay'
                          : 'Tự động bình'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min={1}
                        data-testid="estimating-ups-input"
                        value={
                          activeComp.manualUps != null && Number(activeComp.manualUps) > 0
                            ? activeComp.manualUps
                            : calculationSummary.componentCalculations.find(c => c.id === activeCompId)?.ups || 1
                        }
                        onChange={(e) => {
                          const n = Math.round(Number(e.target.value));
                          updateActiveComponent({
                            manualUps: Number.isFinite(n) && n > 0 ? n : null,
                          });
                        }}
                        className="h-8 text-xs font-mono font-bold bg-white border-indigo-300 px-2.5"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="estimating-auto-ups-btn"
                        onClick={() => updateActiveComponent({ manualUps: null })}
                        disabled={activeComp.manualUps == null}
                        className="h-8 px-2.5 text-[11px] font-semibold shrink-0 bg-white"
                      >
                        Tự động bình
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-xs bg-white">
                <CardHeader className="bg-slate-50/80 py-2 px-3 border-b">
                  <CardTitle className="text-xs font-bold flex items-center gap-1.5 text-slate-800">
                    <Printer className="h-3.5 w-3.5 text-indigo-600" />
                    2. Giấy & Kiểu In Offset
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-2.5 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Loại giấy WMS</Label>
                      <ComboboxConfigMaster
                        categoryCode="paper_types"
                        value={activeComp.paperType}
                        onChange={(v: string) => {
                          const vLower = (v || '').toLowerCase();
                          const isCouche = vLower.includes('couche');
                          const isDuplex = vLower.includes('duplex');
                          const isKraft = vLower.includes('kraft');
                          const defaultPrice = isCouche ? 29000 : (isDuplex ? 22000 : (isKraft ? 20000 : 28500));
                          updateActiveComponent({
                            paperType: v,
                            paperPriceKg: activeComp.paperPriceKg || defaultPrice,
                          });
                        }}
                        placeholder="Chọn hoặc nhập loại giấy..."
                        hideLabel
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Định lượng (gsm)</Label>
                      <Input
                        type="number"
                        value={activeComp.gsm}
                        onChange={e => updateActiveComponent({ gsm: Number(e.target.value) })}
                        className="h-8 text-xs font-mono font-bold px-2.5 bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Kiểu in Offset</Label>
                    <Select
                      value={activeComp.printScheme}
                      onValueChange={(v: any) => updateActiveComponent({ printScheme: v })}
                    >
                      <SelectTrigger className="h-8 text-xs font-semibold text-indigo-950 bg-indigo-50/50 border-indigo-200">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WORK_AND_TURN">In Tự Trở (Work & Turn - Giảm 50% kẽm)</SelectItem>
                        <SelectItem value="SINGLE_SIDED">In 1 Mặt Tiêu Chuẩn</SelectItem>
                        <SelectItem value="SHEETWISE_AB">In Trở Khác Bản (In AB 2 bộ kẽm)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-2 pt-1 border-t">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Màu mặt trước</Label>
                      <Input
                        type="number"
                        value={activeComp.colorsFront}
                        onChange={e => updateActiveComponent({ colorsFront: Number(e.target.value) })}
                        className="h-8 text-xs font-mono px-2.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Màu pha (Pantone)</Label>
                      <Input
                        type="number"
                        value={activeComp.spotColorsFront}
                        onChange={e => updateActiveComponent({ spotColorsFront: Number(e.target.value) })}
                        className="h-8 text-xs font-mono px-2.5 bg-white"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* ═══ PANEL 2 (GIỮA - 5 CỘT): MÔ PHỎNG BÀI BÌNH SVG & BẢNG BÓC TÁCH CHI PHÍ ═══ */}
            <div className="col-span-12 lg:col-span-8 xl:col-span-6 space-y-2.5">
              <Card className="border-slate-200 shadow-xs bg-white overflow-hidden">
                <CardHeader className="bg-slate-50/80 py-2 px-3 border-b flex flex-row items-center justify-between">
                  <CardTitle className="text-xs font-bold flex items-center gap-1.5 text-slate-800">
                    <Layers className="h-3.5 w-3.5 text-indigo-600" />
                    Mô Phỏng Bài Bình Tờ In ({primaryPress.name || 'Khổ 72x102cm - Offset'})
                  </CardTitle>
                  <div className="flex items-center gap-2 font-mono text-xs">
                    <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200 py-0">
                      {calculationSummary.componentCalculations.find(c => c.id === activeCompId)?.layoutType === 'HORIZONTAL' ? 'Xoay 90°' : 'Đứng 0°'}
                    </Badge>
                    <span className="text-slate-600">Số con: <strong className="text-indigo-700" data-testid="estimating-ups-badge">{calculationSummary.componentCalculations.find(c => c.id === activeCompId)?.ups || 1}</strong> ups</span>
                    <span className="text-slate-400">|</span>
                    <span className="text-slate-600">Hao hụt: <strong className="text-amber-700">{calculationSummary.componentCalculations.find(c => c.id === activeCompId)?.wastePct ?? 0}%</strong></span>
                    <span className="text-slate-400">|</span>
                    <span className="text-slate-600">Tờ in: <strong className="text-emerald-700">{calculationSummary.componentCalculations.find(c => c.id === activeCompId)?.grossSheets.toLocaleString() || 0}</strong></span>
                  </div>
                </CardHeader>
                <CardContent className="p-2.5 bg-slate-950 flex items-center justify-center">
                  <svg
                    viewBox="0 0 540 260"
                    preserveAspectRatio="xMidYMid meet"
                    className="w-full h-36 border border-slate-800 bg-slate-900 rounded shadow-inner"
                  >
                    <rect x="0" y="0" width="540" height="14" fill="#f59e0b" fillOpacity="0.4" />
                    <text x="270" y="10.5" fill="#fbbf24" fontSize="9" textAnchor="middle" fontWeight="bold">
                      NHÍP IN (GRIPPER MARGIN 15mm)
                    </text>

                    <line x1="20" y1="26" x2="20" y2="90" stroke="#38bdf8" strokeWidth="2" />
                    <text x="25" y="60" fill="#38bdf8" fontSize="8" fontWeight="bold">SỚ GIẤY (MD)</text>

                    {(() => {
                      const currentCompCalc = calculationSummary.componentCalculations.find(c => c.id === activeCompId);
                      const totalUps = currentCompCalc?.ups || 1;
                      const renderedUps = Math.min(totalUps, MAX_SVG_PREVIEW_CELLS);
                      const cols = Math.max(
                        1,
                        renderedUps < totalUps
                          ? Math.ceil(Math.sqrt(renderedUps))
                          : currentCompCalc?.cols || 1,
                      );
                      const rows = Math.max(1, Math.ceil(renderedUps / cols));
                      const isHorizontal = currentCompCalc?.layoutType === 'HORIZONTAL';

                      const availableW = 500;
                      const availableH = 220;
                      const gap = renderedUps > 16 ? 4 : 8;
                      const cellW = Math.max(24, Math.floor((availableW - (cols - 1) * gap) / cols));
                      const cellH = Math.max(18, Math.floor((availableH - (rows - 1) * gap) / rows));
                      const startX = 20 + Math.max(0, Math.floor((availableW - (cols * cellW + (cols - 1) * gap)) / 2));
                      const startY = 25 + Math.max(0, Math.floor((availableH - (rows * cellH + (rows - 1) * gap)) / 2));

                      return (
                        <>
                          {Array.from({ length: renderedUps }).map((_, i) => {
                            const col = i % cols;
                            const row = Math.floor(i / cols);
                            const x = startX + col * (cellW + gap);
                            const y = startY + row * (cellH + gap);

                            return (
                              <g key={i}>
                                <rect
                                  x={x}
                                  y={y}
                                  width={cellW}
                                  height={cellH}
                                  fill="#1e293b"
                                  stroke="#6366f1"
                                  strokeWidth="1.5"
                                  rx="3"
                                />
                                {cellW >= 36 && cellH >= 22 && (
                                  <>
                                    <rect x={x + 4} y={y + 4} width={Math.max(0, cellW - 8)} height={Math.max(0, cellH - 8)} fill="none" stroke="#475569" strokeDasharray="3,3" />
                                    <text x={x + cellW / 2} y={y + cellH / 2} fill="#e2e8f0" fontSize="9" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">
                                      Con #{i + 1} {isHorizontal ? '(90°)' : ''}
                                    </text>
                                  </>
                                )}
                              </g>
                            );
                          })}
                          {totalUps > MAX_SVG_PREVIEW_CELLS && (
                            <text x="530" y="252" fill="#94a3b8" fontSize="9" textAnchor="end" fontWeight="bold">
                              Hiển thị {MAX_SVG_PREVIEW_CELLS}/{totalUps} con
                            </text>
                          )}
                        </>
                      );
                    })()}
                  </svg>
                </CardContent>
              </Card>

              {/* BẢNG BÓC TÁCH CHI PHÍ ĐA THÀNH PHẦN */}
              <Card className="border-slate-200 shadow-xs bg-white">
                <CardHeader className="bg-slate-50/80 py-2 px-3 border-b flex flex-row items-center justify-between">
                  <CardTitle className="text-xs font-bold text-slate-800">
                    Bảng Bóc Tách Chi Phí Đa Tầng Minh Bạch
                  </CardTitle>
                  <span className="text-xs text-slate-500 font-mono">Định mức: <strong className="text-emerald-700">{marginPercent}% Margin</strong></span>
                </CardHeader>
                <CardContent className="p-0">
                  <Table className="text-xs">
                    <TableHeader>
                      <TableRow className="bg-slate-100/80 h-8">
                        <TableHead className="py-1 px-2 font-bold text-slate-700">Thành Phần</TableHead>
                        <TableHead className="py-1 px-1.5 text-center font-bold text-slate-700">Số Con</TableHead>
                        <TableHead className="py-1 px-2 text-right font-bold text-slate-700">Tiền Giấy</TableHead>
                        <TableHead className="py-1 px-2 text-right font-bold text-slate-700">Kẽm CTP</TableHead>
                        <TableHead className="py-1 px-2 text-right font-bold text-slate-700">In & Gia Công</TableHead>
                        <TableHead className="py-1 px-2 text-right font-bold text-slate-900">Thành Tiền</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {calculationSummary.componentCalculations.map((c, i) => (
                        <TableRow key={c.id} className="h-8.5 hover:bg-slate-50">
                          <TableCell className="py-1.5 px-2 font-bold text-slate-800 whitespace-normal break-words leading-snug">✦ {i + 1}. {c.name}</TableCell>
                          <TableCell className="py-1.5 px-1.5 text-center font-mono tabular-nums">{c.ups} con</TableCell>
                          <TableCell className="py-1.5 px-2 text-right font-mono tabular-nums">{c.paperCost.toLocaleString()} đ</TableCell>
                          <TableCell className="py-1.5 px-2 text-right font-mono tabular-nums">{c.platesCost.toLocaleString()} đ</TableCell>
                          <TableCell className="py-1.5 px-2 text-right font-mono tabular-nums">{(c.printCost + c.postPressCost).toLocaleString()} đ</TableCell>
                          <TableCell className="py-1.5 px-2 text-right font-bold font-mono tabular-nums text-indigo-900">{c.compTotalCost.toLocaleString()} đ</TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="h-8.5 bg-amber-50/50">
                        <TableCell colSpan={5} className="py-1.5 px-2 font-semibold text-slate-700 whitespace-normal leading-snug">
                          Lắp Ráp Hoàn Thiện & Đóng Gói Chung (Dán khay + Thùng 5L)
                        </TableCell>
                        <TableCell className="py-1.5 px-2 text-right font-bold font-mono tabular-nums text-amber-800">
                          {calculationSummary.generalFinishingCost.toLocaleString()} đ
                        </TableCell>
                      </TableRow>
                      <TableRow className="h-9 bg-slate-900 hover:bg-slate-900 text-white font-bold">
                        <TableCell colSpan={3} className="py-2 px-2 text-xs text-white whitespace-normal leading-snug">
                          TỔNG GIÁ VỐN (COGM): <span className="text-amber-400 font-mono text-sm whitespace-nowrap">{calculationSummary.unitCost.toLocaleString()} đ/bộ</span>
                        </TableCell>
                        <TableCell colSpan={3} className="py-2 px-2 text-right text-xs text-emerald-400 whitespace-normal leading-snug">
                          GIÁ BÁN THAM KHẢO: <span className="text-emerald-300 font-mono text-sm font-black whitespace-nowrap">{calculationSummary.unitPrice.toLocaleString()} đ/bộ</span>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </div>

            {/* ═══ PANEL 3 (PHẢI - 3.5 CỘT): GIA CÔNG SAU IN & TÀI CHÍNH ═══ */}
            <div className="col-span-12 lg:col-span-12 xl:col-span-3 space-y-2.5">
              <Card className="border-slate-200 shadow-xs bg-white">
                <CardHeader className="bg-slate-50/80 py-2 px-3 border-b">
                  <CardTitle className="text-xs font-bold flex items-center gap-1.5 text-slate-800">
                    <Scissors className="h-3.5 w-3.5 text-indigo-600" />
                    Gia Công: [{activeComp.name}]
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-2.5 text-xs">
                  <div>
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Cán màng</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <Select
                        value={activeComp.laminationSides}
                        onValueChange={(v: any) => updateActiveComponent({ laminationSides: v })}
                      >
                        <SelectTrigger className="h-8 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NONE">Không cán</SelectItem>
                          <SelectItem value="ONE_SIDE">1 Mặt</SelectItem>
                          <SelectItem value="TWO_SIDES">2 Mặt</SelectItem>
                        </SelectContent>
                      </Select>
                      <ComboboxConfigMaster
                        categoryCode="coatings"
                        value={activeComp.laminationType}
                        onChange={(v: string) => updateActiveComponent({ laminationType: v })}
                        placeholder="Chọn hoặc nhập loại màng..."
                        hideLabel
                      />
                    </div>
                  </div>

                  <div className="pt-2 border-t space-y-1.5">
                    <span className="text-xs font-bold text-slate-700 block">Ép Kim & Hiệu Ứng</span>
                    <div className="grid grid-cols-2 gap-2">
                      <label className="flex items-center gap-2 text-xs cursor-pointer py-1 px-1.5 rounded hover:bg-slate-50 border border-slate-100">
                        <Checkbox
                          checked={activeComp.foilGold}
                          onCheckedChange={(v: any) => updateActiveComponent({ foilGold: !!v })}
                        />
                        <span className="font-medium">Nhũ Vàng</span>
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer py-1 px-1.5 rounded hover:bg-slate-50 border border-slate-100">
                        <Checkbox
                          checked={activeComp.foilSilver}
                          onCheckedChange={(v: any) => updateActiveComponent({ foilSilver: !!v })}
                        />
                        <span className="font-medium">Nhũ Bạc</span>
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer py-1 px-1.5 rounded hover:bg-slate-50 border border-slate-100">
                        <Checkbox
                          checked={activeComp.hasSpotUv}
                          onCheckedChange={(v: any) => updateActiveComponent({ hasSpotUv: !!v })}
                        />
                        <span className="font-medium">Spot UV</span>
                      </label>
                      <label className="flex items-center gap-2 text-xs cursor-pointer py-1 px-1.5 rounded hover:bg-slate-50 border border-slate-100">
                        <Checkbox
                          checked={activeComp.hasEmbossing}
                          onCheckedChange={(v: any) => updateActiveComponent({ hasEmbossing: !!v })}
                        />
                        <span className="font-medium">Dập Nổi 3D</span>
                      </label>
                    </div>
                  </div>

                  <div className="pt-2 border-t space-y-1">
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Bế khuôn / Cắt thành phẩm</Label>
                    <Select
                      value={activeComp.diecutType || 'STANDARD'}
                      onValueChange={(v: any) => updateActiveComponent({ diecutType: v, hasAutoStripping: v !== 'NONE' })}
                    >
                      <SelectTrigger className="h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="NONE">Không bế (Cắt xén thẳng)</SelectItem>
                        <SelectItem value="STANDARD">Bế khuôn tiêu chuẩn</SelectItem>
                        <SelectItem value="DEMI">Bế demi (Decal)</SelectItem>
                        <SelectItem value="PERFORATION">Bế răng cưa</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="pt-2 border-t space-y-1">
                    <Label className="text-xs font-semibold text-slate-600 mb-1 block">Bồi Carton / Sóng</Label>
                    <ComboboxConfigMaster
                      categoryCode="wave_types"
                      value={activeComp.mountingType}
                      onChange={(v: string) => updateActiveComponent({ mountingType: v })}
                      placeholder="Chọn loại sóng bồi..."
                      hideLabel
                    />
                  </div>
                </CardContent>
              </Card>

              <Card className="border-slate-200 shadow-xs bg-white">
                <CardHeader className="bg-slate-50/80 py-2 px-3 border-b">
                  <CardTitle className="text-xs font-bold flex items-center gap-1.5 text-slate-800">
                    <Wand2 className="h-3.5 w-3.5 text-indigo-600" />
                    Đóng Gói & Lợi Nhuận
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-3 space-y-2.5 text-xs">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Lắp ráp (đ/bộ)</Label>
                      <Input
                        type="number"
                        value={assemblyCostPerUnit}
                        onChange={e => setAssemblyCostPerUnit(Number(e.target.value))}
                        className="h-8 text-xs font-mono font-bold px-2.5 bg-white"
                      />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">Đóng gói</Label>
                      <ComboboxConfigMaster
                        categoryCode="packaging_types"
                        value={packagingType}
                        onChange={(v: string) => setPackagingType(v)}
                        placeholder="Quy cách đóng gói..."
                        hideLabel
                      />
                    </div>
                  </div>

                  {targetOrderQty < calculationSummary.moqQty && (
                    <div className="pt-2 border-t space-y-1">
                      <Label className="text-xs font-semibold text-slate-600 mb-1 block">
                        Phân bổ phí lên bài (SL &lt; {calculationSummary.moqQty.toLocaleString()})
                      </Label>
                      <Select
                        value={pricingMode}
                        onValueChange={(v: any) => setPricingMode(v)}
                      >
                        <SelectTrigger className="h-8 text-xs bg-indigo-50/40 border-indigo-200 font-medium">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="MOQ_STANDARD">
                            Theo định mức lô chuẩn (MOQ {calculationSummary.moqQty.toLocaleString()})
                          </SelectItem>
                          <SelectItem value="EXACT_BATCH">
                            Gánh trọn phí lô lẻ ({targetOrderQty.toLocaleString()} sp)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  <div className="pt-2 border-t flex items-center justify-between gap-2">
                    <label className="flex items-center gap-2 text-xs cursor-pointer text-indigo-700 font-bold">
                      <Checkbox checked={hasWindowPatching} onCheckedChange={(v: any) => setHasWindowPatching(!!v)} />
                      <span>Dán Suppo PET</span>
                    </label>
                    <div className="flex items-center gap-1.5 bg-emerald-50 px-2.5 py-1 rounded-md border border-emerald-300 shadow-2xs">
                      <span className="text-xs font-bold text-emerald-900">Margin:</span>
                      <Input
                        type="number"
                        value={marginPercent}
                        onChange={e => setMarginPercent(Number(e.target.value))}
                        className="h-7 w-16 text-xs font-mono font-black text-center bg-white border-emerald-400 text-emerald-950 px-1"
                      />
                      <span className="text-xs font-bold text-emerald-900">%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* ── FOOTER MODAL ── */}
          <DialogFooter className="px-4 py-2.5 bg-slate-100 border-t border-slate-200 flex items-center justify-between flex-none h-12">
            <div className="text-xs text-slate-600 flex items-center gap-2">
              <span className="text-emerald-700 font-bold flex items-center gap-1">
                <CheckCircle2 className="h-3.5 w-3.5" /> 100% Prepress Verified
              </span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-500">Chi phí tính toán từ BOM & Quy trình xưởng in thực tế</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={onClose} className="h-8 text-xs font-medium px-4">
                Đóng
              </Button>
              <Button
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold h-8 text-xs gap-1.5 shadow-sm px-4"
                onClick={handleSaveAndApply}
                disabled={applyMutation.isPending || isLoadingDraft}
              >
                {applyMutation.isPending ? (
                  <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin" /> Đang Lưu...
                  </>
                ) : (
                  <>
                    <Save className="h-3.5 w-3.5" /> Lưu Dự Toán (Giá Vốn)
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── DIALOG IN BẢNG BÓC TÁCH DỰ TOÁN KỸ THUẬT (BM-KT-01) ── */}
      <Dialog open={isPrintPreviewOpen} onOpenChange={setIsPrintPreviewOpen}>
        <DialogContent className="w-[92vw] max-w-[92vw] sm:max-w-[920px] max-h-[90vh] overflow-y-auto p-6 bg-white text-slate-900">
          <DialogTitle className="sr-only">Biểu Mẫu In Bảng Bóc Tách Dự Toán Giá Thành BM-KT-01</DialogTitle>
          <DialogDescription className="sr-only">Biểu mẫu A4 in bảng bóc tách chi phí giá thành sản xuất </DialogDescription>
          <div className="border-b pb-4 mb-4 flex justify-between items-start">
            <div>
              <h2 className="text-lg font-black text-slate-900 uppercase">CÔNG TY TNHH ART PRESTO VIỆT NAM</h2>
              <p className="text-xs text-slate-500">Phòng Kỹ Thuật & Quản Trị Dự Toán Sản Xuất</p>
            </div>
            <div className="text-right">
              <h3 className="text-base font-bold text-indigo-700">BẢNG BÓC TÁCH DỰ TOÁN GIÁ THÀNH</h3>
              <p className="text-xs font-mono text-slate-500">Ngày lập: {new Date().toLocaleDateString('vi-VN')}</p>
              <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300 text-[10px] mt-1">ĐÃ PHÊ DUYỆT BÀI BÌNH</Badge>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 p-3 bg-slate-50 rounded-lg text-xs mb-4 border font-mono">
            <div>
              <span className="text-slate-500 block">Sản phẩm:</span>
              <strong className="text-slate-900 font-sans">{productName || draftData?.quotationDetail?.productName}</strong>
            </div>
            <div>
              <span className="text-slate-500 block">Số lượng sản xuất:</span>
              <strong className="text-indigo-700 text-sm">{targetOrderQty.toLocaleString()} bộ</strong>
            </div>
            <div>
              <span className="text-slate-500 block">Giá bán tham khảo:</span>
              <strong className="text-emerald-700 text-sm">{calculationSummary.unitPrice.toLocaleString()} đ/bộ</strong>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">I. Bóc Tách Chi Tiết Từng Thành Phần</h4>
            {calculationSummary.componentCalculations.map((comp, idx) => (
              <div key={comp.id} className="border rounded p-3 text-xs space-y-2 bg-slate-50/50">
                <div className="flex justify-between items-center border-b pb-1">
                  <span className="font-bold text-slate-900">✦ Thành phần #{idx + 1}: {comp.name}</span>
                  <span className="font-mono text-indigo-700 font-bold">{comp.compTotalCost.toLocaleString()} đ</span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-[11px] text-slate-600 font-mono">
                  <div>Kích thước: <strong>{comp.flatWidth}x{comp.flatLength}mm</strong></div>
                  <div>Sớ giấy: <strong>{comp.grainDirection}</strong></div>
                  <div>Số con/bài: <strong>{comp.ups} ups</strong></div>
                  <div>Tờ in mộc: <strong>{comp.grossSheets.toLocaleString()} tờ</strong></div>
                  <div>Loại giấy: <strong>{comp.paperType} {comp.gsm}g</strong></div>
                  <div>Tiền giấy: <strong>{comp.paperCost.toLocaleString()} đ</strong></div>
                  <div>Kẽm CTP: <strong>{comp.platesCost.toLocaleString()} đ</strong></div>
                  <div>In & Gia công: <strong>{(comp.printCost + comp.postPressCost).toLocaleString()} đ</strong></div>
                </div>
              </div>
            ))}

            <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider pt-2">II. Tổng Hợp Chi Phí Giá Thành Sản Xuất (COGM)</h4>
            <Table className="text-xs border">
              <TableBody>
                <TableRow>
                  <TableCell className="font-semibold whitespace-normal">1. Tổng chi phí Giấy Mộc:</TableCell>
                  <TableCell className="text-right font-mono font-bold">{calculationSummary.totalPaperCost.toLocaleString()} đ</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold whitespace-normal">2. Chi phí Kẽm CTP & Khuôn Bế:</TableCell>
                  <TableCell className="text-right font-mono font-bold">{calculationSummary.totalPlatesCost.toLocaleString()} đ</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold whitespace-normal">3. Chi phí In Offset & Gia công sau in:</TableCell>
                  <TableCell className="text-right font-mono font-bold">{(calculationSummary.totalPrintCost + calculationSummary.totalPostPressCost).toLocaleString()} đ</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="font-semibold whitespace-normal">4. Chi phí Lắp ráp hoàn thiện & Đóng gói:</TableCell>
                  <TableCell className="text-right font-mono font-bold">{calculationSummary.generalFinishingCost.toLocaleString()} đ</TableCell>
                </TableRow>
                <TableRow className="bg-slate-100 font-bold">
                  <TableCell className="text-sm whitespace-normal">TỔNG GIÁ VỐN SẢN XUẤT (COGM):</TableCell>
                  <TableCell className="text-right font-mono text-sm text-indigo-700">{calculationSummary.totalFactoryCost.toLocaleString()} đ ({calculationSummary.unitCost.toLocaleString()} đ/bộ)</TableCell>
                </TableRow>
                <TableRow className="bg-emerald-50 font-bold">
                  <TableCell className="text-sm text-emerald-900 whitespace-normal">ĐƠN GIÁ CHÀO BÁN ĐỀ XUẤT (Margin {marginPercent}%):</TableCell>
                  <TableCell className="text-right font-mono text-base text-emerald-700">{calculationSummary.unitPrice.toLocaleString()} đ/bộ</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>

          <div className="grid grid-cols-3 gap-6 pt-10 text-center text-xs">
            <div>
              <p className="font-bold">Người Lập Dự Toán</p>
              <p className="text-[10px] text-slate-400 mt-0.5">(Ký và ghi rõ họ tên)</p>
              <div className="h-16" />
            </div>
            <div>
              <p className="font-bold">Trưởng Phòng Prepress</p>
              <p className="text-[10px] text-slate-400 mt-0.5">(Xác nhận bài bình & sớ giấy)</p>
              <div className="h-16" />
            </div>
            <div>
              <p className="font-bold">Giám Đốc Phê Duyệt</p>
              <p className="text-[10px] text-slate-400 mt-0.5">(Phê duyệt đơn giá chào bán)</p>
              <div className="h-16" />
            </div>
          </div>

          <DialogFooter className="mt-6 border-t pt-4">
            <Button variant="outline" size="sm" onClick={() => setIsPrintPreviewOpen(false)}>
              Đóng
            </Button>
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold"
              onClick={() => window.print()}
            >
              <PrinterIcon className="h-4 w-4 mr-1.5" /> In Ngay (Print A4)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
